import { getSabineSources } from "@/lib/sabine-chat/sources";

export const AI_CHAT_FEEDBACK_PENDING_ANSWER =
  "No response captured yet.";

/**
 * Display name for each `AIChatFeedbackSource`. Lives here rather than in a
 * component so the source filter, the summary matrix and the card/thread views
 * cannot drift apart (they used to disagree on GENERIC).
 */
export const AI_CHAT_FEEDBACK_SOURCE_LABELS = {
  PLAYBOOK: "Playbook",
  RESULT_COACH: "Result coach",
  TRANSCRIPT_COACH: "Transcript coach",
  REPORTS: "Reports",
  DEAL_CLINIC: "Deal Clinic",
  HOME_SABINE: "Home dashboard",
  EMAIL: "Email",
  GENERIC: "Unclassified",
};

export const AI_CHAT_FEEDBACK_CAPTURE_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  ERROR: "error",
};

const AI_CHAT_FEEDBACK_CAPTURE_STATUS_VALUES = new Set(
  Object.values(AI_CHAT_FEEDBACK_CAPTURE_STATUS)
);

/**
 * Clamps a raw `captureStatus` to a known enum value. Callers may send an
 * absent or unexpected value; rather than trusting it (which can drive the
 * persistence path to overwrite a real answer with the PENDING placeholder),
 * an unrecognized status is derived from whether a real answer is present,
 * mirroring how the normal path infers status.
 *
 * @param {unknown} rawStatus The caller-supplied captureStatus.
 * @param {Object} options
 * @param {boolean} options.hasRealAnswer Whether a non-empty, non-placeholder
 *   answer is present.
 * @returns {"pending"|"completed"|"error"} A valid capture status.
 */
export function normalizeAIChatFeedbackCaptureStatus(
  rawStatus,
  { hasRealAnswer } = {}
) {
  if (
    typeof rawStatus === "string" &&
    AI_CHAT_FEEDBACK_CAPTURE_STATUS_VALUES.has(rawStatus)
  ) {
    return rawStatus;
  }

  return hasRealAnswer
    ? AI_CHAT_FEEDBACK_CAPTURE_STATUS.COMPLETED
    : AI_CHAT_FEEDBACK_CAPTURE_STATUS.PENDING;
}

export function buildAIChatFeedbackMessageId(source, userMessageId) {
  const normalizedSource =
    typeof source === "string" && source.trim() ? source.trim() : "GENERIC";
  const normalizedMessageId =
    typeof userMessageId === "string" && userMessageId.trim()
      ? userMessageId.trim()
      : String(userMessageId || "unknown");

  return `${normalizedSource}:chat:${normalizedMessageId}`;
}

/**
 * Stable feedback `messageId` for one inbound Sabine email. Keyed by the Gmail
 * message id so a retried delivery of the same email updates the row it already
 * wrote instead of creating a duplicate.
 *
 * @param {string} gmailMessageId The Gmail message id from the inbound payload.
 * @returns {string} The unique feedback message id.
 */
export function buildAIChatFeedbackEmailMessageId(gmailMessageId) {
  const normalized =
    typeof gmailMessageId === "string" && gmailMessageId.trim()
      ? gmailMessageId.trim()
      : String(gmailMessageId || "unknown");

  return `EMAIL:gmail:${normalized}`;
}

/** Groups every turn of one Gmail thread into a single conversation thread. */
export function buildAIChatFeedbackEmailConversationId(gmailThreadId) {
  const normalized =
    typeof gmailThreadId === "string" && gmailThreadId.trim()
      ? gmailThreadId.trim()
      : String(gmailThreadId || "unknown");

  return `sabine-email:${normalized}`;
}

function isLocalhostUrl(value) {
  if (typeof value !== "string" || !value.trim()) return false;
  return /(^|\/\/|\.)localhost(?::|\/|$)|127\.0\.0\.1|\[?::1\]?/.test(value);
}

export function shouldCaptureAIChatFeedback() {
  if (process.env.NODE_ENV !== "production") {
    return false;
  }

  const configuredUrls = [
    process.env.NEXTAUTH_URL,
    process.env.NEXT_PUBLIC_APP_URL,
    process.env.APP_URL,
    process.env.VERCEL_URL,
    process.env.VERCEL_PROJECT_PRODUCTION_URL,
  ].filter(Boolean);

  return !configuredUrls.some(isLocalhostUrl);
}

/**
 * The feedback source a coach chat files under, from the record it is about:
 * the `recordKind` a coach page sends (`SIMULATION_RESULT`,
 * `TRANSCRIPT_RESULT`, `COG_REPORT`) or a shared thread's context type, read
 * from the one table in lib/sabine-chat/sources.js. Any other kind is a
 * simulation result's scenario type (`pitch`, `discovery`): RESULT_COACH.
 */
export function resolveChatFeedbackSource(kind) {
  return getSabineSources(kind)?.feedback || "RESULT_COACH";
}

/** A message's key in its thread: its id, or its role and position. */
export function getChatMessageKey(message, index) {
  return message?.id || `${message?.role || "message"}-${index}`;
}

/**
 * The feedback record an assistant message belongs to. The server files each
 * turn under the user message it answered (`<source>:chat:<user message id>`,
 * `startChatFeedbackCapture`), so a thumbs vote on the answer updates that row
 * instead of creating a second one.
 *
 * @param {Array} messages The thread
 * @param {number} index Where the assistant message sits in it
 * @param {string} source The chat's AIChatFeedbackSource
 * @returns {{ assistantMessageId: string, userMessageId: string|null, feedbackMessageId: string, question: string, answer: string }}
 */
export function describeAssistantTurn(messages, index, source) {
  const message = messages[index];
  const assistantMessageId = getChatMessageKey(message, index);
  let userMessage = null;
  let userMessageId = null;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === "user") {
      userMessage = messages[i];
      userMessageId = getChatMessageKey(userMessage, i);
      break;
    }
  }

  return {
    assistantMessageId,
    userMessageId,
    feedbackMessageId: userMessageId
      ? buildAIChatFeedbackMessageId(source, userMessageId)
      : assistantMessageId,
    question:
      message?.question || userMessage?.content || userMessage?.text || "",
    answer: typeof message?.content === "string" ? message.content : "",
  };
}
